import { supabase } from './supabase';
import { Employee, Payroll } from '../types';
import { governmentDeductionsService, GovernmentDeduction } from './governmentDeductionsService';
import { deductionService } from './deductionService';
import { calculateNetPay } from '../utils/philippinePayrollUtils';

export interface BankPayrollEmployee {
  employee_id: string;
  employee_number: string;
  employee_name: string;
  bank_account: string;
  department: string;
  position: string;
  basic_salary: number;
  overtime_pay: number;
  bonuses: number;
  holiday_pay: number;
  gross_pay: number;
  sss_contribution: number;
  philhealth_contribution: number;
  pagibig_contribution: number;
  withholding_tax: number;
  other_deductions: number;
  total_deductions: number;
  net_pay: number;
  payment_status: 'pending' | 'processed' | 'paid' | 'failed';
}

export interface BankPayrollSummary {
  period: string;
  total_employees: number;
  total_gross_pay: number;
  total_deductions: number;
  total_net_pay: number;
  employees: BankPayrollEmployee[];
  generated_at: string;
}

export interface BankPayrollExport {
  bank_account: string;
  employee_name: string;
  net_pay: number;
  reference_number?: string;
}

class BankPayrollService {
  /**
   * Get current payroll data for bank processing
   */
  async getCurrentBankPayroll(payPeriodStart?: string, payPeriodEnd?: string): Promise<BankPayrollSummary> {
    try {
      // If no period specified, use current month
      const currentDate = new Date();
      const defaultStart = payPeriodStart || new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString().split('T')[0];
      const defaultEnd = payPeriodEnd || new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).toISOString().split('T')[0];

      // Get all active employees
      const { data: employees, error: employeesError } = await supabase
        .from('employees')
        .select('*')
        .eq('status', 'active')
        .order('last_name', { ascending: true });

      if (employeesError) {
        console.error('Error fetching employees:', employeesError);
        throw employeesError;
      }

      if (!employees || employees.length === 0) {
        return {
          period: `${defaultStart} to ${defaultEnd}`,
          total_employees: 0,
          total_gross_pay: 0,
          total_deductions: 0,
          total_net_pay: 0,
          employees: [],
          generated_at: new Date().toISOString()
        };
      }

      // Get existing payroll records for this period
      const { data: payrollRecords, error: payrollError } = await supabase
        .from('payrolls')
        .select('*')
        .gte('pay_period_start', defaultStart)
        .lte('pay_period_end', defaultEnd);

      if (payrollError) {
        console.error('Error fetching payroll records:', payrollError);
        // Continue without existing payroll data
      }

      const bankPayrollEmployees: BankPayrollEmployee[] = [];
      let totalGrossPay = 0;
      let totalDeductions = 0;
      let totalNetPay = 0;

      for (const employee of employees) {
        // Check if payroll already exists for this employee and period
        const existingPayroll = payrollRecords?.find(p => p.employee_id === employee.id);
        
        let bankEmployee: BankPayrollEmployee;

        if (existingPayroll) {
          // Use existing payroll data
          bankEmployee = {
            employee_id: employee.id,
            employee_number: employee.id.slice(-6).toUpperCase(),
            employee_name: `${employee.first_name} ${employee.last_name}`,
            bank_account: employee.bank_account || '',
            department: employee.department,
            position: employee.position,
            basic_salary: existingPayroll.basic_salary,
            overtime_pay: existingPayroll.overtime_pay || 0,
            bonuses: existingPayroll.bonuses || 0,
            holiday_pay: (existingPayroll.holiday_pay || 0) + (existingPayroll.holiday_additional_pay || 0),
            gross_pay: existingPayroll.gross_pay,
            sss_contribution: existingPayroll.sss_contribution,
            philhealth_contribution: existingPayroll.philhealth_contribution,
            pagibig_contribution: existingPayroll.pagibig_contribution,
            withholding_tax: existingPayroll.withholding_tax,
            other_deductions: existingPayroll.other_deductions || 0,
            total_deductions: existingPayroll.total_deductions,
            net_pay: existingPayroll.net_pay,
            payment_status: existingPayroll.payment_status as 'pending' | 'processed' | 'paid' | 'failed'
          };
        } else {
          // Calculate payroll data on the fly
          const governmentDeductions = governmentDeductionsService.calculateEmployeeDeductions(employee);
          const otherDeductionsCalc = await deductionService.calculatePayrollDeductions(employee.id);
          
          const payCalculation = calculateNetPay({
            basicSalary: employee.salary,
            overtimePay: 0,
            bonuses: 0,
            thirteenthMonthPay: 0,
            holidayPay: 0,
            holidayAdditionalPay: 0,
            sssContribution: governmentDeductions.sss_contribution,
            philhealthContribution: governmentDeductions.philhealth_contribution,
            pagibigContribution: governmentDeductions.pagibig_contribution,
            withholdingTax: governmentDeductions.withholding_tax,
            otherDeductions: otherDeductionsCalc.total_deductions
          });

          bankEmployee = {
            employee_id: employee.id,
            employee_number: employee.id.slice(-6).toUpperCase(),
            employee_name: `${employee.first_name} ${employee.last_name}`,
            bank_account: employee.bank_account || '',
            department: employee.department,
            position: employee.position,
            basic_salary: employee.salary,
            overtime_pay: 0,
            bonuses: 0,
            holiday_pay: 0,
            gross_pay: payCalculation.grossPay,
            sss_contribution: governmentDeductions.sss_contribution,
            philhealth_contribution: governmentDeductions.philhealth_contribution,
            pagibig_contribution: governmentDeductions.pagibig_contribution,
            withholding_tax: governmentDeductions.withholding_tax,
            other_deductions: otherDeductionsCalc.total_deductions,
            total_deductions: payCalculation.totalDeductions,
            net_pay: payCalculation.netPay,
            payment_status: 'pending'
          };
        }

        bankPayrollEmployees.push(bankEmployee);
        totalGrossPay += bankEmployee.gross_pay;
        totalDeductions += bankEmployee.total_deductions;
        totalNetPay += bankEmployee.net_pay;
      }

      return {
        period: `${defaultStart} to ${defaultEnd}`,
        total_employees: bankPayrollEmployees.length,
        total_gross_pay: Math.round(totalGrossPay * 100) / 100,
        total_deductions: Math.round(totalDeductions * 100) / 100,
        total_net_pay: Math.round(totalNetPay * 100) / 100,
        employees: bankPayrollEmployees,
        generated_at: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error in getCurrentBankPayroll:', error);
      throw error;
    }
  }

  /**
   * Get bank payroll export data (simplified format for bank upload)
   */
  async getBankPayrollExport(payPeriodStart?: string, payPeriodEnd?: string): Promise<BankPayrollExport[]> {
    try {
      const bankPayroll = await this.getCurrentBankPayroll(payPeriodStart, payPeriodEnd);
      
      return bankPayroll.employees
        .filter(emp => emp.bank_account && emp.net_pay > 0)
        .map(emp => ({
          bank_account: emp.bank_account,
          employee_name: emp.employee_name,
          net_pay: emp.net_pay,
          reference_number: `PAY-${emp.employee_number}-${new Date().getFullYear()}${(new Date().getMonth() + 1).toString().padStart(2, '0')}`
        }));
    } catch (error) {
      console.error('Error in getBankPayrollExport:', error);
      throw error;
    }
  }

  /**
   * Get employees without bank accounts
   */
  async getEmployeesWithoutBankAccounts(): Promise<Employee[]> {
    try {
      const { data: employees, error } = await supabase
        .from('employees')
        .select('*')
        .eq('status', 'active')
        .or('bank_account.is.null,bank_account.eq.')
        .order('last_name', { ascending: true });

      if (error) {
        console.error('Error fetching employees without bank accounts:', error);
        throw error;
      }

      return employees || [];
    } catch (error) {
      console.error('Error in getEmployeesWithoutBankAccounts:', error);
      throw error;
    }
  }

  /**
   * Update payment status for employees
   */
  async updatePaymentStatus(employeeIds: string[], status: 'pending' | 'processed' | 'paid' | 'failed', payPeriodStart: string, payPeriodEnd: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('payrolls')
        .update({ payment_status: status })
        .in('employee_id', employeeIds)
        .gte('pay_period_start', payPeriodStart)
        .lte('pay_period_end', payPeriodEnd);

      if (error) {
        console.error('Error updating payment status:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updatePaymentStatus:', error);
      throw error;
    }
  }

  /**
   * Get bank payroll summary by department
   */
  async getBankPayrollByDepartment(payPeriodStart?: string, payPeriodEnd?: string): Promise<{ [department: string]: BankPayrollSummary }> {
    try {
      const bankPayroll = await this.getCurrentBankPayroll(payPeriodStart, payPeriodEnd);
      const departmentSummaries: { [department: string]: BankPayrollSummary } = {};

      bankPayroll.employees.forEach(employee => {
        const dept = employee.department;
        
        if (!departmentSummaries[dept]) {
          departmentSummaries[dept] = {
            period: bankPayroll.period,
            total_employees: 0,
            total_gross_pay: 0,
            total_deductions: 0,
            total_net_pay: 0,
            employees: [],
            generated_at: bankPayroll.generated_at
          };
        }

        departmentSummaries[dept].employees.push(employee);
        departmentSummaries[dept].total_employees++;
        departmentSummaries[dept].total_gross_pay += employee.gross_pay;
        departmentSummaries[dept].total_deductions += employee.total_deductions;
        departmentSummaries[dept].total_net_pay += employee.net_pay;
      });

      // Round totals
      Object.values(departmentSummaries).forEach(summary => {
        summary.total_gross_pay = Math.round(summary.total_gross_pay * 100) / 100;
        summary.total_deductions = Math.round(summary.total_deductions * 100) / 100;
        summary.total_net_pay = Math.round(summary.total_net_pay * 100) / 100;
      });

      return departmentSummaries;
    } catch (error) {
      console.error('Error in getBankPayrollByDepartment:', error);
      throw error;
    }
  }
}

export const bankPayrollService = new BankPayrollService();